import { supabase } from '@/lib/supabase';

export interface MessageLimitCheck {
  allowed: boolean;
  reason?: 'daily_limit_exceeded' | 'monthly_limit_exceeded' | 'no_limits_configured';
  daily_limit: number;
  monthly_limit: number;
  daily_usage: number;
  monthly_usage: number;
  package: string;
  debug_info?: any;
}

export interface UserUsageStats {
  topic: string;
  daily_usage: number;
  monthly_usage: number;
  daily_limit: number;
  monthly_limit: number;
  package_name: string;
}

/**
 * Check if user can send a message based on subscription limits
 */
export const checkMessageLimit = async (
  userId: string,
  topic: 'guide' | 'performance' | 'trading_strategy' | 'advisor',
  requestId?: string
): Promise<MessageLimitCheck> => {
  try {
    console.log('[USAGE CHECK] Checking subscription limits:', {
      userId: userId.substring(0, 8) + '...',
      topic,
      requestId: requestId || 'no-request-id'
    });

    // Call the real database function
    const { data, error } = await supabase.rpc('ai_assistant_can_send_message', {
      user_uuid: userId,
      topic_param: topic
    });

    console.log('[SERVICE DEBUG] Raw response from Supabase:', {
      data,
      error,
      dataType: typeof data,
      dataKeys: data ? Object.keys(data) : null,
      isArray: Array.isArray(data),
      length: data?.length
    });

    if (error) {
      console.error('[USAGE CHECK] Database error:', error.message);
      throw new Error(`Database error: ${error.message}`);
    }

    if (!data) {
      console.error('[USAGE CHECK] No data returned from database');
      throw new Error('No usage data returned');
    }

    // Fix: Database function returns array, need to access first element
    if (!Array.isArray(data) || data.length === 0) {
      console.error('[USAGE CHECK] Invalid data format - expected non-empty array');
      throw new Error('Invalid usage data format returned');
    }

    const record = data[0]; // Get first record from array

    console.log('[SERVICE DEBUG] Extracted record:', {
      record,
      allowed: record.allowed,
      allowedType: typeof record.allowed
    });

    // Ensure boolean conversion for allowed field
    const result: MessageLimitCheck = {
      allowed: Boolean(record.allowed),
      reason: record.reason as any,
      daily_limit: Number(record.daily_limit) || 0,
      monthly_limit: Number(record.monthly_limit) || 0,
      daily_usage: Number(record.daily_usage) || 0,
      monthly_usage: Number(record.monthly_usage) || 0,
      package: record.package || 'free',
      debug_info: record.debug_info
    };
    
    console.log('[SERVICE DEBUG] Final processed result:', {
      allowed: result.allowed,
      allowedType: typeof result.allowed,
      originalAllowed: record.allowed,
      originalAllowedType: typeof record.allowed
    });
    
    console.log('[USAGE CHECK] Result:', {
      requestId: requestId || 'no-request-id',
      topic,
      allowed: result.allowed,
      reason: result.reason,
      dailyUsage: `${result.daily_usage}/${result.daily_limit}`,
      monthlyUsage: `${result.monthly_usage}/${result.monthly_limit}`,
      package: result.package
    });
    
    return result;
    
  } catch (error) {
    // Enhanced error logging to track undefined errors
    console.error('[USAGE CHECK] Critical error:', {
      error,
      errorType: typeof error,
      errorMessage: error instanceof Error ? error.message : String(error),
      errorStack: error instanceof Error ? error.stack : 'No stack trace',
      userId: userId?.substring(0, 8) + '...',
      topic
    });
    
    // Ensure we always throw a proper Error object, never undefined/null
    const properError = error instanceof Error ? error : new Error(`Usage check failed: ${String(error)}`);
    
    // Return restrictive defaults on error to prevent abuse
    return {
      allowed: false,
      reason: 'no_limits_configured',
      daily_limit: 1,
      monthly_limit: 20,
      daily_usage: 0,
      monthly_usage: 0,
      package: 'free'
    };
  }
};

/**
 * Increment usage counter for successful message
 */
export const incrementMessageUsage = async (
  userId: string,
  topic: 'guide' | 'performance' | 'trading_strategy' | 'advisor',
  requestId?: string
): Promise<boolean> => {
  try {
    console.log('[USAGE INCREMENT] Recording usage:', { 
      userId: userId.substring(0, 8) + '...', 
      topic,
      requestId: requestId || 'undefined'
    });
    
    // Call the real increment function
    const { data, error } = await supabase.rpc('ai_assistant_increment_usage', {
      user_uuid: userId,
      topic_param: topic
    });

    if (error) {
      console.error('[USAGE INCREMENT] Database error:', error.message);
      throw new Error(`Failed to increment usage: ${error.message}`);
    }

    if (data === false) {
      console.error('[USAGE INCREMENT] Function returned false');
      throw new Error('Usage increment failed');
    }

    console.log('[USAGE INCREMENT] Successfully recorded usage:', {
      userId: userId.substring(0, 8) + '...',
      topic,
      requestId
    });
    
    return true;
    
  } catch (error) {
    // Enhanced error logging to track undefined errors
    console.error('[USAGE INCREMENT] Critical error:', {
      error,
      errorType: typeof error,
      errorMessage: error instanceof Error ? error.message : String(error),
      errorStack: error instanceof Error ? error.stack : 'No stack trace',
      userId: userId?.substring(0, 8) + '...',
      topic
    });
    
    // Ensure we always throw a proper Error object, never undefined/null
    const properError = error instanceof Error ? error : new Error(`Usage increment failed: ${String(error)}`);
    throw properError;
  }
};

/**
 * Get real usage statistics based on subscription
 */
export const getUserUsageStatsWithDefaults = async (userId: string): Promise<UserUsageStats[]> => {
  try {
    console.log('[USAGE STATS] Fetching real usage stats for user:', userId.substring(0, 8) + '...');
    
    const { data, error } = await supabase.rpc('ai_assistant_get_usage_stats', {
      user_uuid: userId
    });

    if (error) {
      console.error('[USAGE STATS] Database error:', error.message);
      throw new Error(`Failed to get usage stats: ${error.message}`);
    }

    if (!data || data.length === 0) {
      console.warn('[USAGE STATS] No data returned, using free package defaults');
      // Return free package defaults if no data
      return [
        { topic: 'guide', daily_usage: 0, monthly_usage: 0, daily_limit: 5, monthly_limit: 100, package_name: 'free' },
        { topic: 'performance', daily_usage: 0, monthly_usage: 0, daily_limit: 3, monthly_limit: 60, package_name: 'free' },
        { topic: 'trading_strategy', daily_usage: 0, monthly_usage: 0, daily_limit: 2, monthly_limit: 40, package_name: 'free' },
        { topic: 'advisor', daily_usage: 0, monthly_usage: 0, daily_limit: 1, monthly_limit: 20, package_name: 'free' }
      ];
    }

    const stats = data.map((row: any) => ({
      topic: row.topic,
      daily_usage: Number(row.daily_usage) || 0,
      monthly_usage: Number(row.monthly_usage) || 0,
      daily_limit: row.daily_limit || 0,
      monthly_limit: row.monthly_limit || 0,
      package_name: row.package_name || 'free'
    }));

    console.log('[USAGE STATS] Retrieved stats:', {
      userId: userId.substring(0, 8) + '...',
      package: stats[0]?.package_name,
      topicCount: stats.length
    });

    return stats;
    
  } catch (error) {
    // Enhanced error logging to track undefined errors
    console.error('[USAGE STATS] Critical error:', {
      error,
      errorType: typeof error,
      errorMessage: error instanceof Error ? error.message : String(error),
      errorStack: error instanceof Error ? error.stack : 'No stack trace',
      userId: userId?.substring(0, 8) + '...'
    });
    
    // Return free package defaults on error
    return [
      { topic: 'guide', daily_usage: 0, monthly_usage: 0, daily_limit: 5, monthly_limit: 100, package_name: 'free' },
      { topic: 'performance', daily_usage: 0, monthly_usage: 0, daily_limit: 3, monthly_limit: 60, package_name: 'free' },
      { topic: 'trading_strategy', daily_usage: 0, monthly_usage: 0, daily_limit: 2, monthly_limit: 40, package_name: 'free' },
      { topic: 'advisor', daily_usage: 0, monthly_usage: 0, daily_limit: 1, monthly_limit: 20, package_name: 'free' }
    ];
  }
};

// Keep other functions for compatibility but simplified
export const getUserUsageStats = getUserUsageStatsWithDefaults;

export const getAllMessageLimits = async () => [];
export const updateMessageLimit = async () => ({ id: '', package_name: '', topic: '', daily_limit: 999999, monthly_limit: 999999, created_at: '', updated_at: '' });
export const bulkUpdateMessageLimits = async () => true;
export const getAdminUsageStats = async () => ({ totalUsers: 0, totalMessages: 0, usageByTopic: {}, usageByPackage: {} });
